import React, { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, User, Car, Store, Check, Snowflake } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { authApi } from "../../lib/api";
import { VEHICLE_CATEGORIES } from "../../lib/driver";
import { savePendingSignup } from "./SignupVerify";
import { errorMessage } from "../../lib/api";
import { SmartTag } from "../../components/app/SmartDefaults";
import { withIndiaPrefix, formatIndiaPhoneInput, indiaPhoneDigits, isValidIndiaPhone } from "../../lib/smartDefaults";
import { GoogleButton, startGoogleLogin } from "../../components/auth/GoogleAuth";
import ConsentModal from "../../components/auth/ConsentModal";

const ROLES = [
  { value: "rider", label: "Customer", text: "Book rides & order food", icon: User },
  { value: "driver", label: "Driver", text: "Earn on rides & deliveries", icon: Car },
  { value: "restaurant", label: "Restaurant", text: "List your menu & get orders", icon: Store },
];

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ROLES.some((r) => r.value === params.get("role")) ? params.get("role") : "rider";
  const [form, setForm] = useState({ name: "", email: "", phone: "+91 ", password: "", role: initialRole, vehicle_category: "private_scooter", taxi_scope: "local", has_ac: null, facilities: [], other_facilities: "", channel: "sms" });
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const pendingAction = useRef(null); // "email" | "google"
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doEmailSignup = async () => {
    setBusy(true);
    try {
      // Phone is MANDATORY and OTP-verified for everyone. Email is OPTIONAL — if given we verify it too.
      const digits = indiaPhoneDigits(form.phone);
      if (digits.length !== 10 || !isValidIndiaPhone(form.phone)) { setBusy(false); return toast.error("Enter a valid 10-digit mobile number to create your account"); }
      const email = form.email.trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setBusy(false); return toast.error("Enter a valid email address, or leave it blank"); }
      const phone = `+91${digits}`;
      const isTaxiCar = ["commercial_car", "commercial_jumbo"].includes(form.vehicle_category);
      const isPrivateCar = ["private_car_4", "private_car_6"].includes(form.vehicle_category);
      const driverExtra = form.role === "driver" ? {
        vehicle_category: form.vehicle_category,
        ...(isTaxiCar ? { taxi_scope: form.taxi_scope, ...(form.has_ac != null ? { has_ac: form.has_ac } : {}) } : {}),
        ...(isPrivateCar ? { facilities: form.facilities, ...(form.other_facilities.trim() ? { other_facilities: form.other_facilities.trim() } : {}) } : {}),
      } : {};
      const pending = await authApi.signupStart({ name: form.name.trim(), ...(email ? { email } : {}), password: form.password, role: form.role, phone, accepted_terms: true, ...driverExtra });
      savePendingSignup(pending);
      toast.success(pending.email_provided ? "Verify your phone, then your email" : "Verify your phone to activate your account");
      navigate("/auth/signup/verify", { replace: true, state: pending });
    } catch (err) {
      toast.error(errorMessage(err, "Sign up failed"));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!accepted) { pendingAction.current = "email"; setConsentOpen(true); return; }
    doEmailSignup();
  };

  const onAccept = () => {
    setAccepted(true);
    setConsentOpen(false);
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action === "email") doEmailSignup();
    else if (action === "google") startGoogleLogin(form.role);
  };

  const onDecline = () => {
    setConsentOpen(false);
    pendingAction.current = null;
    toast.error("You need to accept the policies to create an account");
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="One account for rides, food and partner tools."
      footer={<>Already have an account? <Link to="/auth/login" className="font-semibold text-brand hover:underline" data-testid="link-login">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
        <div className="space-y-2">
          <Label>I want to join as</Label>
          <div className="grid grid-cols-3 gap-2">
            {ROLES.map((r) => {
              const active = form.role === r.value;
              return (
                <button type="button" key={r.value} onClick={() => setForm((f) => ({ ...f, role: r.value }))} className={`relative rounded-2xl border p-3 text-left transition-colors duration-200 ${active ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`} data-testid={`role-${r.value}`}>
                  {active && <Check className="absolute right-2 top-2 h-4 w-4 text-brand" />}
                  <r.icon className={`h-5 w-5 ${active ? "text-brand" : "text-ink-muted"}`} />
                  <p className="mt-2 text-sm font-semibold text-ink">{r.label}</p>
                  <p className="text-[11px] leading-tight text-ink-muted">{r.text}</p>
                </button>
              );
            })}
          </div>
        </div>
        {form.role === "driver" && (
          <div className="space-y-2" data-testid="signup-driver-type">
            <Label>What kind of driver are you?</Label>
            <div className="grid gap-2">
              {VEHICLE_CATEGORIES.map((c) => {
                const active = form.vehicle_category === c.value;
                return (
                  <button type="button" key={c.value} onClick={() => setForm((f) => ({ ...f, vehicle_category: c.value }))} className={`relative rounded-xl border p-3 text-left transition-colors duration-200 ${active ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`} data-testid={`driver-cat-${c.value}`}>
                    {active && <Check className="absolute right-2 top-2 h-4 w-4 text-brand" />}
                    <p className="pr-6 text-sm font-semibold text-ink">{c.label}</p>
                    <p className="text-[11px] leading-tight text-ink-muted">{c.role} — {c.desc}</p>
                  </button>
                );
              })}
            </div>
            {["commercial_car", "commercial_jumbo"].includes(form.vehicle_category) && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="signup-taxi-scope">
                <p className="text-sm font-semibold text-ink">Where will you drive?</p>
                <p className="mb-2 text-[11px] text-ink-muted">Local = within-city rides only. Regional = intercity rides only. Both = within-city and intercity.</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "local", label: "Local", desc: "Within-city only" },
                    { value: "regional", label: "Regional", desc: "Intercity only" },
                    { value: "both", label: "Both", desc: "City + intercity" },
                  ].map((s) => {
                    const on = form.taxi_scope === s.value;
                    return (
                      <button type="button" key={s.value} onClick={() => setForm((f) => ({ ...f, taxi_scope: s.value }))} className={`rounded-xl border p-3 text-left transition-colors duration-200 ${on ? "border-sky-500 bg-white ring-1 ring-sky-400" : "border-surface-300 bg-white hover:border-sky-200"}`} data-testid={`taxi-scope-${s.value}`}>
                        <p className="text-sm font-semibold text-ink">{s.label}</p>
                        <p className="text-[11px] leading-tight text-ink-muted">{s.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {["commercial_car", "commercial_jumbo"].includes(form.vehicle_category) && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="signup-ac">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-ink"><Snowflake className="h-4 w-4 text-sky-500" /> Does your car have Air Conditioning?</p>
                <p className="mb-2 text-[11px] text-ink-muted">Optional — AC rides pay a 20% add-on and are offered to AC cars first. You can set this later in Profile.</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm((f) => ({ ...f, has_ac: true }))} className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${form.has_ac === true ? "bg-sky-600 text-white" : "bg-white text-ink-soft border border-surface-300"}`} data-testid="signup-ac-yes">Yes, has AC</button>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, has_ac: false }))} className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${form.has_ac === false ? "bg-graphite text-white" : "bg-white text-ink-soft border border-surface-300"}`} data-testid="signup-ac-no">No AC</button>
                  {form.has_ac != null && <button type="button" onClick={() => setForm((f) => ({ ...f, has_ac: null }))} className="rounded-full px-3 py-2 text-xs font-semibold text-ink-muted hover:text-ink" data-testid="signup-ac-skip">Skip</button>}
                </div>
              </div>
            )}
            {["private_car_4", "private_car_6"].includes(form.vehicle_category) && (
              <div className="rounded-xl border border-surface-300 bg-surface-50 p-3" data-testid="signup-private-facilities">
                <p className="text-sm font-semibold text-ink">What does your car offer?</p>
                <p className="mb-2 text-[11px] text-ink-muted">Pick the features your car has — customers see these when choosing a car.</p>
                <div className="flex flex-wrap gap-2">
                  {["AC", "Sunroof", "Sound System", "Fog lights", "GPS", "Luggage carrier"].map((fac) => {
                    const on = form.facilities.includes(fac);
                    return (
                      <button type="button" key={fac} onClick={() => setForm((f) => ({ ...f, facilities: on ? f.facilities.filter((x) => x !== fac) : [...f.facilities, fac] }))} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-graphite text-white" : "bg-white text-ink-soft border border-surface-300"}`} data-testid={`signup-fac-${fac.replace(/\s+/g, "-").toLowerCase()}`}>{fac}</button>
                    );
                  })}
                </div>
                <p className="mb-1 mt-3 text-[11px] font-semibold text-ink-soft">Other facilities you provide (optional)</p>
                <textarea value={form.other_facilities} onChange={(e) => setForm((f) => ({ ...f, other_facilities: e.target.value }))} rows={2} maxLength={500} placeholder="e.g. bottled water, phone charger, child seat, English-speaking, help with luggage" className="w-full rounded-xl border border-surface-300 bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted" data-testid="signup-other-facilities" />
              </div>
            )}
            <p className="text-[11px] text-ink-muted">This sets your default driver type. You can change vehicle details later in your profile.</p>
          </div>
        )}
        <div className="space-y-2"><Label htmlFor="name">Full name</Label><Input id="name" required value={form.name} onChange={set("name")} placeholder="Your name" className="h-11" data-testid="signup-name" /></div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="email">Email <span className="text-ink-muted">(optional)</span></Label><Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-11" data-testid="signup-email" /></div>
          <div className="space-y-2"><div className="flex items-center justify-between gap-2"><Label htmlFor="phone">Mobile number <span className="text-brand font-semibold">(required)</span></Label>{indiaPhoneDigits(form.phone) === "" && <SmartTag testId="signup-phone-smart" />}</div><Input id="phone" type="tel" required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatIndiaPhoneInput(e.target.value) }))} placeholder="+91 98765 43210" className="h-11" data-testid="signup-phone" />
            <p className="pt-1 text-xs text-ink-muted" data-testid="signup-verify-hint">We'll send a one-time code to your mobile to verify it and activate your account. Email is optional — add it now (we'll verify it) or later from your profile.</p></div>
        </div>
        <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="At least 6 characters" className="h-11" data-testid="signup-password" /></div>
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="signup-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
        <div className="relative py-1 text-center text-xs uppercase tracking-wider text-ink-muted">
          <span className="relative z-10 bg-surface px-3">or</span>
          <span className="absolute inset-x-0 top-1/2 -z-0 h-px bg-surface-300" />
        </div>
        <GoogleButton role={form.role} label={`Continue with Google as ${ROLES.find((r) => r.value === form.role)?.label}`} canProceed={accepted} onBlocked={() => { pendingAction.current = "google"; setConsentOpen(true); }} />
        <p className="text-center text-sm text-ink-soft">Prefer a code? <Link to="/auth/phone" className="font-semibold text-brand hover:underline">Sign up with a one-time code</Link></p>
        <p className="text-center text-xs text-ink-muted">By creating an account you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-brand hover:underline">Terms</a>, <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-brand hover:underline">Privacy</a> &amp; <a href="/charges" target="_blank" rel="noopener noreferrer" className="font-medium text-brand hover:underline">Charges</a>.</p>
      </form>
      <ConsentModal open={consentOpen} onAccept={onAccept} onDecline={onDecline} />
    </AuthLayout>
  );
}
